import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { SLOTS } from "@/components/finance/SavingActions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const TrialForm = ({ offers, postcode, onDone }) => {
  const [v, setV] = useState({ model: offers[0].key, postcode: postcode || "", phone: "", slots: ["soir"] });
  const toggle = (k) => setV({ ...v, slots: v.slots.includes(k) ? v.slots.filter((x) => x !== k) : [...v.slots, k] });
  const send = async (e) => {
    e.preventDefault();
    if (!v.slots.length) return toast.error("Choisissez au moins un créneau");
    try { await api.post("/insights/tesla/trial", v); toast.success("Demande d'essai envoyée"); onDone(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={send} className="space-y-2 rounded-xl bg-background/80 p-3" data-testid="tesla-trial-form">
      <div className="grid gap-2 sm:grid-cols-3">
        <select value={v.model} onChange={(e) => setV({ ...v, model: e.target.value })} className="h-10 rounded-md border border-input bg-background px-2 text-sm" data-testid="trial-model">{offers.map((o) => <option key={o.key} value={o.key}>Tesla {o.name}</option>)}</select>
        <Input value={v.postcode} onChange={(e) => setV({ ...v, postcode: e.target.value })} placeholder="Code postal" maxLength={5} required data-testid="trial-postcode" />
        <Input type="tel" value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} placeholder="Téléphone" required data-testid="trial-phone" />
      </div>
      <div className="flex flex-wrap gap-1.5">{Object.entries(SLOTS).map(([k, l]) => (
        <button type="button" key={k} onClick={() => toggle(k)} className={cn("rounded-full border px-3 py-1.5 text-xs transition-colors", v.slots.includes(k) ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted")} data-testid={`trial-slot-${k}`}>{l}</button>))}</div>
      <Button type="submit" size="sm" className="rounded-full" data-testid="trial-submit">Envoyer ma demande</Button>
    </form>
  );
};

export const TeslaTrial = ({ s, postcode }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const refresh = () => { setOpen(false); qc.invalidateQueries({ queryKey: ["insights-home"] }); };
  const cancel = async () => {
    try { await api.delete("/insights/tesla/trial"); toast.success("Demande annulée"); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const feedback = async (called) => {
    try { await api.put("/insights/tesla/trial/feedback", { called }); toast.success(called ? "Merci pour votre retour" : "Nous relançons nos partenaires"); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (s.trial) return (
    <div className="space-y-2 rounded-xl bg-background/80 p-3 text-sm" data-testid="tesla-trial-status">
      <p className="flex flex-wrap items-center gap-2"><CalendarCheck className="h-4 w-4 text-success" /> Demande d'essai Tesla {s.trial.model_name} envoyée ({s.trial.postcode}) : un professionnel partenaire vous rappellera.
        <button type="button" onClick={cancel} className="text-xs font-bold underline" data-testid="tesla-trial-cancel">Annuler</button></p>
      {s.trial.called === true ? <p className="text-xs font-bold text-success" data-testid="tesla-trial-called">Vous avez été rappelé, bon essai !</p>
        : <div className="flex flex-wrap items-center gap-2" data-testid="tesla-trial-feedback"><span className="text-xs font-semibold">{s.trial.called === false ? "Toujours pas rappelé ?" : "Avez-vous été rappelé ?"}</span>
          <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" onClick={() => feedback(true)} data-testid="tesla-trial-yes">Oui</Button>
          <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" onClick={() => feedback(false)} data-testid="tesla-trial-no">Pas encore</Button></div>}
    </div>
  );
  return open ? <TrialForm offers={s.offers} postcode={postcode} onDone={refresh} />
    : <Button size="sm" variant="secondary" className="rounded-full" onClick={() => setOpen(true)} data-testid="tesla-trial-open"><CalendarCheck className="mr-1 h-4 w-4" /> Demander un essai près de chez moi</Button>;
};
